import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useState, useMemo } from 'react'
import { useEditorStore } from '~/client/components/slide-editor/store'
import { exportSlideToHtml, RICHTEXT_CSS } from '~/client/components/slide-editor/exportHtml'
import type { Slide } from '~/client/components/slide-editor/types'
import HtmlSlide from '~/client/components/present/common/HtmlSlide'
import {
  Carousel,
  CarouselClose,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '~/client/components/ui/carousel'

export const Route = createFileRoute('/_app/slide-present')({
  component: SlidePresent,
})

const BASE_WIDTH = 1920
const BASE_HEIGHT = 1080

// ---------------------------------------------------------------------------
// Individual slide renderer — converts a Slide object to HTML and displays it
// ---------------------------------------------------------------------------

const SlideRenderer: React.FC<{ slide: Slide }> = React.memo(({ slide }) => {
  const html = useMemo(() => exportSlideToHtml(slide), [slide])

  return (
    <div className="slide-present-richtext" style={{ width: '100%', height: '100%' }}>
      <HtmlSlide html={html} />
    </div>
  )
})
SlideRenderer.displayName = 'SlideRenderer'

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function SlidePresent() {
  const navigate = useNavigate()
  const slides = useEditorStore((s) => s.slides)

  const [fadePhase, setFadePhase] = useState<
    'initial' | 'black' | 'presentation'
  >('initial')

  // Fade transition (same as template presentation)
  useEffect(() => {
    const t1 = setTimeout(() => setFadePhase('black'), 10)
    const t2 = setTimeout(() => setFadePhase('presentation'), 600)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [])

  // Responsive scaling + Escape to close
  const [scale, setScale] = useState(1)

  const carouselRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) node.focus()
  }, [])

  const goBack = React.useCallback(() => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: '/' })
    }
  }, [navigate])

  useEffect(() => {
    const slideWidth = slides[0]?.width ?? BASE_WIDTH
    const slideHeight = slides[0]?.height ?? BASE_HEIGHT

    const computeScale = () =>
      setScale(
        Math.min(
          window.innerWidth / slideWidth,
          window.innerHeight / slideHeight,
        ),
      )
    computeScale()
    window.addEventListener('resize', computeScale)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        goBack()
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('resize', computeScale)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [goBack, slides])

  // ------ No slides ------
  if (!slides || slides.length === 0) {
    return (
      <div className="absolute inset-0 w-full h-full z-50 bg-black flex items-center justify-center">
        <div className="text-white text-lg">No slides to present</div>
      </div>
    )
  }

  const slideWidth = slides[0]?.width ?? BASE_WIDTH
  const slideHeight = slides[0]?.height ?? BASE_HEIGHT

  // ------ Full presentation (carousel) ------
  return (
    <div className="absolute inset-0 w-full h-full z-50">
      {/* Inject richtext CSS so exported slide HTML renders correctly */}
      <style dangerouslySetInnerHTML={{ __html: `
        .slide-present-richtext ${RICHTEXT_CSS}
      ` }} />
      {/* Black overlay that fades in */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          fadePhase === 'initial' ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Presentation content that fades in after black screen */}
      <div
        className={`flex justify-center items-center absolute inset-0 transition-opacity duration-500 ${
          fadePhase === 'presentation' ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="w-full h-screen flex items-center justify-center bg-black">
          <div
            style={{
              width: slideWidth,
              height: slideHeight,
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
            }}
          >
            <Carousel
              ref={carouselRef}
              className="relative overflow-hidden"
              opts={{ loop: false, align: 'start' }}
              tabIndex={0}
            >
              <CarouselContent style={{ height: slideHeight }}>
                {slides.map((slide) => (
                  <CarouselItem key={slide.id} style={{ height: slideHeight }}>
                    <SlideRenderer slide={slide} />
                  </CarouselItem>
                ))}
              </CarouselContent>

              <CarouselPrevious className="left-4 top-1/2 -translate-y-1/2 z-50 bg-white/80 shadow hover:bg-white" />
              <CarouselNext className="right-4 top-1/2 -translate-y-1/2 z-50 bg-white/80 shadow hover:bg-white" />
              <CarouselClose
                onClick={goBack}
                className="shadow hover:bg-white"
              />
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  )
}
